import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import { errorCode } from '@/api/errors';
import type { components } from '@/api/qraft/schema';
import { AlertBox } from '@/common/AlertBox';
import { FormFieldLabel } from '@/common/forms/FormInput';
import { InfoSectionContainer } from '@/common/InfoSection';
import { LargeArrowBackButton } from '@/common/LargeArrowBackButton';
import { TabsBar } from '@/common/TabsBar';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import { WBSButton } from '@/common/WBSButton';
import {
  ReassignConfirmModal,
  SKIP_REASON_KEYS,
} from '@/groups/coordinator/ReassignConfirmModal';
import { useAdminTabs } from '@/groups/coordinator/useAdminTabs';
import { PageContent } from '@/settings/settingsStyles';
import {
  PEOPLE_PICKER_DEBOUNCE_MS,
  PEOPLE_PICKER_MIN_QUERY,
  PEOPLE_PICKER_PAGE_SIZE,
  type Person,
  useAsyncPeoplePicker,
} from '~/hooks/useAsyncPeoplePicker';
import { SearchSelect } from '~/styles/forms/SearchSelect';
import { GroupHeaderWrapper } from '~/styles/layoutElements';
import { formatString } from '~/utils/stringFormat';

type ReassignPreview = components['schemas']['ReassignPreview'];
type ReassignStudentsRequest = components['schemas']['ReassignStudentsRequest'];
type ReassignStudentsResult = components['schemas']['ReassignStudentsResult'];

/**
 * The success banner's two forms. Both keys ARE minted by this group — legacy's
 * own message was never `__()`-wrapped (raw interpolation at
 * `class-reassign-students-ext.php:42-43`) and neither store carries it.
 *
 * A count ternary, not an i18next plural: all four locale files hold zero
 * `_one`/`_other` keys and `i18n.ts` declares no plural config, so the in-tree
 * recipe is two keys chosen on the number (`MyStudentsTable.tsx:244-245`).
 */
const COUNT_KEYS = {
  one: 'groups.d-student-reassigned-successfully',
  other: 'groups.d-students-reassigned-successfully',
} as const;

/**
 * `forbidden` is the reachable refusal: the `/admin/*` layout guard admits the
 * admin of ANY group type (`useIsGroupAdmin.ts:17-19`), so a study-helper study
 * group's admin gets this screen fully rendered with two working pickers and
 * only the two POSTs refuse. Everything else — including the 400
 * `reassign_target_not_in_group` — falls to the generic, which is right: the
 * picker cannot produce that code through the UI, only a stale session can.
 *
 * Title + body, as at `EditStudentTagsModal.tsx:128-139`: the title alone reads
 * as a heading with no sentence under it. The body is NOT that file's
 * `groups.tag-permission-denied-body` — that copy is member-tag-specific ("edit
 * this student … the study helper of the student"). `wbs.you-dont-have-permission`
 * is the generic one, and it is complete in all four locales (WPML unit 22118,
 * `wbs` domain, 4 of 4 languages), so nothing is minted.
 */
const FAILURE_COPY = {
  forbidden: {
    title: 'groups.tag-permission-denied-title',
    body: 'wbs.you-dont-have-permission',
  },
} as const;

/**
 * Both of legacy's select2 sources for this screen call
 * `wbs_select2_user_search_results()` with no `$text` override
 * (`functions/theme-actions.php:436-453` and `:479-497`), so both fall to its
 * default `CONCAT(first_name,' ',last_name,' (#',user_id,')')`
 * (`functions/select2-helpers.php:32`). Duplicate display names are real on both
 * axes — one congregation derives two students both displaying the same name,
 * and another offers two same-named targets — so the id is what makes an option
 * selectable. W2.4 D12.
 */
const pickerLabel = (displayName: string, id: number): string =>
  `${displayName} (#${id})`;

const noop = (): void => {};

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/admin/reassign-students',
)({
  component: ReassignStudentsScreen,
  // Same guard, and for the same reason, as the Substitute screen's
  // (`$groupName.admin.substitute.tsx`), where W2.5's review found it: this
  // screen shipped in W2.4 without one. `Number('x')` is NaN and `NaN != null`
  // is true, so a junk `?reassign=` engaged the deep-link branch at `:393`/`:398`
  // — picker disabled — while `student_id=NaN` 422s, leaving `deepLinked`
  // undefined so `:225` returns before preselecting anything. An empty locked
  // field that refuses every submit. `isInteger` rather than `isFinite` because
  // `?reassign=1.5` serializes as `student_id=1.5` and 422s identically; `> 0`
  // drops `?reassign=0`, which previously preselected a locked "Deleted User".
  validateSearch: (search): { reassign?: number } => {
    const id = Number(search.reassign);
    return { reassign: Number.isInteger(id) && id > 0 ? id : undefined };
  },
});

function ReassignStudentsScreen() {
  const { t } = useTranslation();
  const { groupName } = Route.useParams();
  const { reassign } = Route.useSearch();
  const navigate = useNavigate();
  const adminTabs = useAdminTabs();

  const { data: groupData } = api.getGroupBySlug.useQuery({
    path: { group_slug: groupName },
  });
  const groupId = groupData?.id ?? 0;

  // What the person is typing, and what the query is keyed on. Both pickers
  // debounce the way legacy's select2 does (`delay: 250`); without it every
  // keystroke past the third is its own request.
  const [studentTerm, setStudentTerm] = useState('');
  const [studentSearch, setStudentSearch] = useState('');
  const [helperTerm, setHelperTerm] = useState('');
  const [helperSearch, setHelperSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(
      () => setStudentSearch(studentTerm),
      PEOPLE_PICKER_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [studentTerm]);

  useEffect(() => {
    const timer = setTimeout(
      () => setHelperSearch(helperTerm),
      PEOPLE_PICKER_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [helperTerm]);

  const { data: studentMatches, isFetching: studentsFetching } =
    api.getGroupStudents.useQuery(
      {
        path: { group_id: groupId },
        query: { search: studentSearch, limit: PEOPLE_PICKER_PAGE_SIZE },
      },
      {
        enabled: groupId > 0 && studentSearch.length >= PEOPLE_PICKER_MIN_QUERY,
      },
    );

  const { data: helperMatches, isFetching: helpersFetching } =
    api.getGroupStudyHelpers.useQuery(
      {
        path: { group_id: groupId },
        query: { search: helperSearch, limit: PEOPLE_PICKER_PAGE_SIZE },
      },
      {
        enabled: groupId > 0 && helperSearch.length >= PEOPLE_PICKER_MIN_QUERY,
      },
    );

  const studentResults = useMemo<Person[]>(
    () =>
      (studentMatches?.data ?? []).map((student) => ({
        id: student.id,
        name: pickerLabel(student.display_name, student.id),
      })),
    [studentMatches],
  );

  // Legacy's target picker is not "any member": the select carries
  // `data-filter="group-admins-mods"`
  // (`buddypress/groups/single/admin/reassign-students.php:33`), whose SQL is
  // `AND (is_mod = 1 OR is_admin = 1) AND is_confirmed = 1`
  // (`functions/theme-actions.php:479-497`). Filtered here rather than by a
  // `role` param on `get_group_study_helpers`: that operation is W2.3's shipped
  // surface with three admin call sites, and this group produces no `admin/**`
  // diff. The residual is an under-filled page if a congregation ever holds
  // `member`-role helpers — zero do — and it can never become a wrong write,
  // because the server refuses a non-leader target. W2.4 D13.
  const helperResults = useMemo<Person[]>(
    () =>
      (helperMatches?.data ?? [])
        .filter((member) => member.role === 'admin' || member.role === 'mod')
        .map((member) => ({
          id: member.id,
          name: pickerLabel(member.display_name, member.id),
        })),
    [helperMatches],
  );

  // The control stays in its loading state through the debounce window too, so
  // it does not flash "no results" between a keystroke and the request it
  // caused.
  const studentPicker = useAsyncPeoplePicker(
    {
      results: studentResults,
      loading:
        studentTerm.length >= PEOPLE_PICKER_MIN_QUERY &&
        (studentTerm !== studentSearch || studentsFetching),
      onSearch: setStudentTerm,
    },
    true,
  );
  const helperPicker = useAsyncPeoplePicker(
    {
      results: helperResults,
      loading:
        helperTerm.length >= PEOPLE_PICKER_MIN_QUERY &&
        (helperTerm !== helperSearch || helpersFetching),
      onSearch: setHelperTerm,
    },
    false,
  );

  // `?reassign=<id>` — emitted by the congregation Students-Waiting widget and
  // its table, mirroring legacy's single emitter
  // (`congregation-students-waiting-widget.php:127`). Resolved through the
  // picker's own endpoint by id, so a deep link into another congregation's
  // student resolves to nothing here rather than leaking a name.
  const { data: deepLinked } = api.getGroupStudents.useQuery(
    { path: { group_id: groupId }, query: { student_id: reassign } },
    { enabled: reassign != null && groupId > 0 },
  );

  const { preselect: preselectStudent } = studentPicker;
  // Applied once per deep-linked id: a successful write clears both pickers and
  // invalidates `getGroupStudents`, and without this the refetch would put the
  // just-moved student straight back into the (disabled) field.
  const appliedDeepLink = useRef<number | null>(null);

  useEffect(() => {
    if (reassign == null || deepLinked === undefined) return;
    if (appliedDeepLink.current === reassign) return;
    appliedDeepLink.current = reassign;
    const row = deepLinked.data[0];
    preselectStudent(
      row
        ? { id: row.id, name: pickerLabel(row.display_name, row.id) }
        : // Legacy renders `Deleted User` for an id it cannot resolve; the id
          // suffix is kept so the locked field reads like a searched selection
          // and names which id the link carried (D12).
          {
            id: reassign,
            name: pickerLabel(t('actions.deleted-user'), reassign),
          },
    );
  }, [reassign, deepLinked, preselectStudent, t]);

  const [confirming, setConfirming] = useState<{
    body: ReassignStudentsRequest;
    preview: ReassignPreview;
  } | null>(null);
  const [result, setResult] = useState<ReassignStudentsResult | null>(null);
  const [selectionInvalid, setSelectionInvalid] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  const { mutateAsync: previewReassign, isPending: isPreviewing } =
    api.previewGroupStudentsReassign.useMutation();
  const { mutateAsync: reassignStudents, isPending: isReassigning } =
    api.reassignGroupStudents.useMutation();

  const handleSave = async (): Promise<void> => {
    setFailure(null);
    setResult(null);
    const studentIds = studentPicker.value.map(Number);
    const helperValue = helperPicker.value[0];
    if (studentIds.length === 0 || helperValue === undefined) {
      setSelectionInvalid(true);
      return;
    }
    setSelectionInvalid(false);
    // The same body goes to both operations, as legacy posts the same form
    // twice: the write re-evaluates every id and applies the eligible subset
    // (D4), so a student who became ineligible between Save and Confirm comes
    // back in `skipped[]` rather than being written.
    const body: ReassignStudentsRequest = {
      student_ids: studentIds,
      study_helper_id: Number(helperValue),
    };
    try {
      const preview = await previewReassign({
        path: { group_id: groupId },
        body,
      });
      setConfirming({ body, preview });
    } catch (error) {
      setFailure(error);
    }
  };

  const handleConfirm = async (): Promise<void> => {
    if (confirming === null) return;
    setFailure(null);
    try {
      const written = await reassignStudents({
        path: { group_id: groupId },
        body: confirming.body,
      });
      setConfirming(null);
      setResult(written);
      studentPicker.onChange([]);
      helperPicker.onChange([]);
      await Promise.all([
        api.getGroupStudents.invalidateQueries(),
        // Not just the students list: `GroupMemberItem.students_adopted` reads
        // `students.study_helper_id`, the column this write moves, so the group
        // Members table shows stale counts on BOTH the old and the new helper
        // until this key is invalidated too.
        api.getGroupStudyHelpers.invalidateQueries(),
      ]);
    } catch (error) {
      setConfirming(null);
      setFailure(error);
    }
  };

  const failureCode = failure !== null ? errorCode(failure) : undefined;
  const failureCopy =
    failureCode !== undefined && failureCode in FAILURE_COPY
      ? FAILURE_COPY[failureCode as keyof typeof FAILURE_COPY]
      : undefined;

  return (
    <PageContent>
      <TabsBar tabs={adminTabs} base="/groups/$groupName/admin" />
      <GroupHeaderWrapper>
        <TitleWithQuestion title={t('lessons.reassign-students')} />
      </GroupHeaderWrapper>
      {/* Legacy's handler emits the CAPITALISED wording of this sentence
          (`group-extentions-hooks.php:64` → catalogue unit 125456); the web
          bundle carries only the lowercase unit 22541, which is what this key
          holds. Both units are complete in ES/FR/PT_BR, so reusing the
          lowercase one costs nothing but the capital letter — minting a
          near-duplicate key for it would cost a four-locale edit. W2.4 D11. */}
      {selectionInvalid && (
        <AlertBox variant="error">
          {t('lessons.please-select-at-least')}
        </AlertBox>
      )}
      {failure !== null && (
        <AlertBox variant="error">
          <div>
            <strong>
              {failureCopy
                ? t(failureCopy.title)
                : t('wbs.something-went-wrong-please')}
            </strong>
            {failureCopy && <div>{t(failureCopy.body)}</div>}
          </div>
        </AlertBox>
      )}
      {result !== null && (
        <AlertBox variant="success">
          <div>
            {/* The count EXCLUDES skips — legacy increments `$success_count`
                inside the skip guard (`class-reassign-students-ext.php:38`) — so
                it is not the length of the selection. */}
            <strong>
              {formatString(
                t(result.reassigned === 1 ? COUNT_KEYS.one : COUNT_KEYS.other),
                String(result.reassigned),
              )}
            </strong>
            {/* Legacy swallows its skips; naming them is D6. This is the only
                place a WRITE-time skip can surface. */}
            {result.skipped.length > 0 && (
              <SkippedList>
                {result.skipped.map((skipped) => (
                  <li key={skipped.student_id}>
                    {skipped.display_name || t('actions.deleted-user')} —{' '}
                    {t(SKIP_REASON_KEYS[skipped.reason])}
                  </li>
                ))}
              </SkippedList>
            )}
          </div>
        </AlertBox>
      )}
      <InfoBox>
        <LargeArrowBackButton
          onClick={() =>
            navigate({
              to: '/groups/$groupName/admin/group-avatar/',
              params: { groupName },
            })
          }
        />
        <Field>
          <FormFieldLabel>{t('lessons.students')}</FormFieldLabel>
          {/* Locked on a deep link, as legacy renders the select `disabled` with
              the id in a hidden input
              (`buddypress/groups/single/admin/reassign-students.php:14-31`).
              The `onChange` is neutralised as well as the control disabled:
              `SearchSelect` still renders each chip's remove button while
              disabled, so the lock would otherwise be one click from being
              cleared. Fixing that in the control is W2.2's file, not this
              group's. */}
          <SearchSelect
            multi
            disabled={reassign != null}
            loading={studentPicker.loading}
            minQueryLength={PEOPLE_PICKER_MIN_QUERY}
            options={studentPicker.options}
            value={studentPicker.value}
            onChange={reassign != null ? noop : studentPicker.onChange}
            onSearch={studentPicker.onSearch}
          />
        </Field>
        <Field>
          <FormFieldLabel>{t('lessons.study-helper')}</FormFieldLabel>
          <SearchSelect
            multi={false}
            loading={helperPicker.loading}
            minQueryLength={PEOPLE_PICKER_MIN_QUERY}
            options={helperPicker.options}
            value={helperPicker.value}
            onChange={helperPicker.onChange}
            onSearch={helperPicker.onSearch}
          />
        </Field>
        <Footer>
          <WBSButton
            type="button"
            wide
            isLoading={isPreviewing}
            onClick={() => {
              void handleSave();
            }}
          >
            {t('actions.save')}
          </WBSButton>
        </Footer>
      </InfoBox>
      {confirming !== null && (
        <ReassignConfirmModal
          preview={confirming.preview}
          isConfirming={isReassigning}
          onConfirm={() => {
            void handleConfirm();
          }}
          onCancel={() => setConfirming(null)}
        />
      )}
    </PageContent>
  );
}

const InfoBox = styled(InfoSectionContainer)`
  position: relative;
`;

const Field = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
`;

const SkippedList = styled.ul`
  margin: 8px 0 0 0;
  padding-left: 20px;
`;
